import React from "react";
import { motion } from "framer-motion";
import {
  Image as ImageIcon,
  Plus,
  Star,
  List,
  Percent,
  LineChart,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import type { TFunction } from "i18next";
import Image from "next/image";

function ProductDetailPhone({ dir, t }: { dir: "rtl" | "ltr"; t: TFunction }) {
  return (
    <div
      className="relative mx-auto w-full max-w-[280px] sm:max-w-[300px]"
      role="img"
      aria-hidden="true"
      dir={dir}
    >
      <div className="rounded-[2.25rem] border-[6px] border-gray-900 bg-gray-900 p-1.5 shadow-2xl shadow-gray-900/15">
        <div className="relative flex h-[580px] flex-col overflow-hidden rounded-[1.75rem] bg-white sm:h-[620px]">
          {/* Hero image */}
          <div className="relative flex h-36 shrink-0 items-center justify-center bg-[#c45c2a]">
            {/* <span className="text-5xl" aria-hidden="true">
              🍖
            </span> */}
            <span className="text-5xl z-10" aria-hidden="true">
              <Image
                src="/images/meat.png"
                alt="Product 1"
                width={120}
                height={120}
              />
            </span>
            <span className="absolute start-3 top-3 rounded-full bg-black/30 px-2.5 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
              {t("richItems.mockup.photoCount")}
            </span>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 space-y-4 overflow-hidden px-4 pb-4 pt-3 text-start">
              {/* Title & price */}
              <div className="flex items-start justify-between gap-3">
                <h3>
                  <span className="text-base font-bold text-gray-900">
                    {t("richItems.mockup.productName")}
                  </span>
                </h3>
                <div className="shrink-0 text-end">
                  <div className="text-[10px] text-gray-400 line-through">
                    $60.00
                  </div>
                  <div className="text-sm font-bold text-[#FF00A4]">$50.00</div>
                </div>
              </div>

              <p>
                <span className="text-[11px] leading-relaxed text-gray-500">
                  {t("richItems.mockup.description")}
                </span>
              </p>

              {/* Meta */}
              <div className="flex flex-wrap gap-3 text-[10px] text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <span className="text-[#FF00A4]">⏱</span>{" "}
                  {t("richItems.mockup.calories")}
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="text-gray-400">◇</span>{" "}
                  {t("richItems.mockup.allergens")}
                </span>
              </div>

              {/* Variations */}
              <div>
                <p className="mb-2">
                  <span className="text-[11px] font-semibold text-gray-800">
                    {t("richItems.mockup.variation")}
                  </span>
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full border border-[#FF00A4] px-3 py-1 text-[10px] font-semibold text-[#FF00A4]">
                    {t("richItems.mockup.regular")}
                  </span>
                  <span className="rounded-full border border-gray-200 px-3 py-1 text-[10px] font-medium text-gray-500">
                    {t("richItems.mockup.large")}
                  </span>
                </div>
              </div>

              {/* Required choice */}
              <div>
                <p className="mb-2">
                  <span className="text-[11px] font-semibold text-gray-800">
                    {t("richItems.mockup.chooseSauce")}{" "}
                    <span className="font-bold text-[#FF00A4]">
                      {t("richItems.mockup.required")}
                    </span>
                  </span>
                </p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-[11px] text-gray-800">
                    <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-[#FF00A4]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#FF00A4]" />
                    </span>
                    {t("richItems.mockup.greenSauce")}
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-gray-500">
                    <span className="h-3.5 w-3.5 rounded-full border-2 border-gray-300" />
                    {t("richItems.mockup.smokyBbq")}
                  </label>
                </div>
              </div>
            </div>

            {/* Sticky footer */}
            <div className="flex shrink-0 items-center gap-3 border-t border-gray-100 px-4 py-3">
              <div className="flex items-center gap-3 rounded-full border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-700">
                <span className="text-gray-400">−</span>
                <span>1</span>
                <span className="text-[#FF00A4]">+</span>
              </div>
              <button
                type="button"
                className="flex-1 rounded-xl bg-[#FF00A4] py-2.5 text-center text-xs font-bold text-white cursor-default"
                tabIndex={-1}
              >
                {t("richItems.mockup.add")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const FEATURE_KEYS = ["f1", "f2", "f3", "f4", "f5", "f6"] as const;

const FEATURE_ICONS = {
  f1: ImageIcon,
  f2: List,
  f3: Plus,
  f4: Percent,
  f5: Star,
  f6: LineChart,
} as const;

export default function MenoRichItems() {
  const { t, isRTL } = useLanguage();
  const textAlign = "text-start";

  const itemReveal = {
    hidden: { opacity: 0, y: 24 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: "easeOut" },
    },
  };

  return (
    <section className="bg-white py-16 lg:py-24 safari-paint-contain">
      <motion.div
        className="custom-container"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        variants={{
          hidden: { opacity: 0, y: 40 },
          show: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, staggerChildren: 0.1 },
          },
        }}
      >
        <div
          className={`flex flex-col-reverse items-stretch gap-12 lg:flex-row lg:items-center lg:gap-16 xl:gap-24`}
        >
          {/* Phone mockup */}
          <motion.div
            className={`flex w-full shrink-0 justify-center lg:w-[38%] xl:w-[36%] ${
              isRTL ? "lg:justify-start" : "lg:justify-end"
            }`}
            variants={itemReveal}
          >
            <ProductDetailPhone dir={isRTL ? "rtl" : "ltr"} t={t} />
          </motion.div>

          {/* Content */}
          <motion.div
            className={`w-full flex-1 ${textAlign}`}
            variants={itemReveal}
          >
            <span className="inline-flex items-center bg-[#FFE1EC] text-[#FF00A4] text-xs font-bold tracking-wide px-4 py-1.5 rounded-full mb-5">
              {t("richItems.badge")}
            </span>

            <h2>
              <span
                className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight"
                style={{
                  fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
                }}
              >
                {t("richItems.title1")}{" "}
                <span className="text-[#FF00A4]">
                  "{t("richItems.titleAccent")}"
                </span>{" "}
                {t("richItems.title2")}
              </span>
            </h2>

            <p className="mt-4 max-w-none lg:max-w-xl">
              <span
                className="text-lg leading-relaxed text-gray-600"
                style={{
                  fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
                }}
              >
                {t("richItems.desc")}
              </span>
            </p>

            <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-10">
              {FEATURE_KEYS.map((key) => {
                const Icon = FEATURE_ICONS[key];
                return (
                  <div key={key} className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#FFE1EC]">
                      <Icon
                        aria-hidden="true"
                        focusable="false"
                        className="h-5 w-5 text-[#FF00A4]"
                      />
                    </div>
                    <div className={textAlign}>
                      <h3 style={{ lineHeight: 0 }}>
                        <span
                          className="text-sm font-bold text-gray-900"
                          style={{
                            fontFamily:
                              '"Inter", ui-sans-serif, system-ui, sans-serif',
                          }}
                        >
                          {t(`richItems.${key}.title`)}
                        </span>
                      </h3>
                      <p className="mt-1">
                        <span
                          className="text-sm leading-relaxed text-gray-500"
                          style={{
                            fontFamily:
                              '"Inter", ui-sans-serif, system-ui, sans-serif',
                          }}
                        >
                          {t(`richItems.${key}.desc`)}
                        </span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
